import {
  IBasicDashboardInfo,
  IBasicReportView,
  IJob,
  IMarkoutAccountRow,
  IMarkoutReportData,
  IMarkoutRiskTotals,
  IMarkoutSymbolRow,
  IReportPeriod,
  IReportSettings,
  IRiskCard,
  RiskLevel,
  makeSymbolRowId,
  parseIntervalToMs,
  startOfUtcDay,
  DAY_MS,
} from '@cbridge-packages/markout';
import { IApiDashboardInfo, IApiJob, IApiReport, IApiReportPeriod, IApiReportSettings } from '../_entities/IApiReport';
import { IApiMarkoutAccount } from '../_entities/IApiMarkoutAccount';
import { IApiMarkoutSymbol } from '../_entities/IApiMarkoutSymbol';
import { IApiMarkoutRiskCard, IApiMarkoutTotals } from '../_entities/IApiMarkoutTotals';
import { IApiGetReportDataRes } from '../_interfaces/IApiGetReportData';

export const apiPeriodToPeriod = (api: IApiReportPeriod): IReportPeriod => {
  // `interval` is the inclusive number of days the report covers and `api.end` (epoch ms) is the
  // last moment of the final day. Derive `start` as the beginning of the first covered day, so the
  // displayed Start/End dates and the day count (`end - start` rounded to days) all line up.
  const intervalDays = Math.max(1, Math.round(parseIntervalToMs(api.interval) / DAY_MS));
  const start = startOfUtcDay(api.end) - (intervalDays - 1) * DAY_MS;
  return { start, end: api.end };
};

export const apiSettingsToSettings = (api: IApiReportSettings): IReportSettings => ({
  priceType: api.priceType,
  reportPeriod: apiPeriodToPeriod(api.reportPeriod),
  servers: api.servers,
  accounts: api.accounts,
  accountGroups: api.accountGroups,
  side: api.side,
  minNotional: api.minNotional,
  symbolGroups: api.symbolGroups,
  symbols: api.symbols,
});

export const apiJobToJob = (api: IApiJob): IJob => ({
  id: api.id,
  startedAt: api.startedAt,
  finishedAt: api.finishedAt,
  status: api.status,
  maxRisk: api.maxRisk,
  riskCount: api.riskCount,
});

export const apiReportToReport = (api: IApiReport): IBasicReportView => ({
  id: api.id,
  name: api.name,
  createdBy: api.createdBy,
  createdAt: api.createdAt,
  settings: apiSettingsToSettings(api.settings),
  jobs: api.jobs.map(apiJobToJob),
});

export const apiDashboardInfoToDashboardInfo = (api: IApiDashboardInfo | null): IBasicDashboardInfo | null => (
  api
    ? {
      maxRisk: api.maxRisk ?? undefined,
      riskCount: api.riskCount ?? undefined,
      period: apiPeriodToPeriod(api.period),
    }
    : null
);

export const apiAccountToAccount = (api: IApiMarkoutAccount): IMarkoutAccountRow => ({
  serverId: api.serverId,
  serverName: api.serverName,
  accountId: api.accountId,
  accountGroup: api.accountGroup,
  tradesCount: api.tradesCount,
  usdAmount: api.usdAmount,
  usdAmountIncomplete: api.usdAmountIncomplete,
  intervals: api.intervals,
  result: api.result,
});

const apiCardToCard = (riskLevel: RiskLevel, api: IApiMarkoutRiskCard): IRiskCard => ({
  riskLevel,
  accountsCount: api.accountsCount,
  dealsCount: api.dealsCount,
  usdAmount: api.usdAmount,
  usdAmountIncomplete: api.usdAmountIncomplete,
});

export const apiTotalsToTotals = (api: IApiMarkoutTotals): IMarkoutRiskTotals => ({
  [RiskLevel.Critical]: apiCardToCard(RiskLevel.Critical, api.riskCritical),
  [RiskLevel.High]: apiCardToCard(RiskLevel.High, api.riskHigh),
  [RiskLevel.Medium]: apiCardToCard(RiskLevel.Medium, api.riskMedium),
  [RiskLevel.Low]: apiCardToCard(RiskLevel.Low, api.riskLow),
  [RiskLevel.All]: apiCardToCard(RiskLevel.All, api.riskLevelAll),
});

export const apiReportDataToReportData = (api: IApiGetReportDataRes['data']): IMarkoutReportData => ({
  accounts: api?.accounts?.map(apiAccountToAccount) ?? [],
  totals: api?.totals ? apiTotalsToTotals(api.totals) : {},
});

export const apiSymbolToSymbol = (
  api: IApiMarkoutSymbol,
  serverId: string,
  accountId: string,
): IMarkoutSymbolRow => ({
  id: makeSymbolRowId(serverId, accountId, api.symbolName),
  symbolName: api.symbolName,
  symbolGroup: api.symbolGroup,
  tradesCount: api.tradesCount,
  usdAmount: api.usdAmount,
  usdAmountIncomplete: api.usdAmountIncomplete,
  intervals: api.intervals,
  result: api.result,
});
